import { apiService } from "./api";
import { API_CONFIG } from "../config/api";
import type { Product } from "../types";

const BASE = API_CONFIG.ENDPOINTS.PRODUCTS.BASE; // "/products"

// ── Types ──────────────────────────────────────────────────────────────────

export interface MergeCandidate {
    product1_id: string;
    product1_name: string;
    product1_sku: string;
    product1_price: number;
    product2_id: string;
    product2_name: string;
    product2_sku: string;
    product2_price: number;
    match_score: number;
    match_criteria: {
        name_similarity?: number;
        same_category?: boolean;
        same_brand?: boolean;
        price_similarity?: number;
        sku_match?: boolean;
        total_score?: number;
    };
}

export type MergeableField =
    | "name"
    | "description"
    | "sku"
    | "barcode"
    | "cost_price"
    | "selling_price"
    | "weight"
    | "gross_weight"
    | "dimensions"
    | "category_id"
    | "brand_id"
    | "is_featured"
    | "is_visible";

export interface ProductMergeRequest {
    master_product_id: string;
    duplicate_product_id: string;
    merge_reason?: string;
    /** Maps field name → "master" | "duplicate".  Absent = keep master value. */
    field_overrides?: Partial<Record<MergeableField, "master" | "duplicate">>;
}

export interface MergeLogEntry {
    merge_id: string;
    master_product_id: string;
    merged_product_id: string;
    merged_product_data: Record<string, unknown>;
    field_overrides: Record<string, string>;
    merge_reason: string | null;
    merged_by_user_id: string | null;
    merge_date: string;
}

// ── Service ────────────────────────────────────────────────────────────────

class ProductMergeService {
    async getCandidates(
        product_id?: string,
        min_score = 0.6,
        limit = 50,
    ): Promise<MergeCandidate[]> {
        const params = new URLSearchParams({ min_score: String(min_score), limit: String(limit) });
        if (product_id) params.set("product_id", product_id);
        return apiService.get<MergeCandidate[]>(`${BASE}/merge/candidates?${params}`);
    }

    async mergeProducts(request: ProductMergeRequest): Promise<Product> {
        return apiService.post<Product>(`${BASE}/merge`, request);
    }

    async getMergeHistory(product_id: string): Promise<MergeLogEntry[]> {
        return apiService.get<MergeLogEntry[]>(`${BASE}/merge-history/${product_id}`);
    }
}

export const productMergeService = new ProductMergeService();
